import React from 'react';
import { Image, Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Plus, X } from './icons';
import { colors, formes, radius, spacing } from '../theme/tokens';
import { FEATURE_ACCENTS } from '../theme/featureAccents';

export interface GrilleCapturesProps {
  images: { uri: string }[];
  // Nombre d'emplacements (5 pour un profil LinkedIn).
  max: number;
  onAdd: () => void;
  onRemove: (index: number) => void;
  libelleAjouter: string;
  libelleRetirer: (numero: number) => string;
  // testID du premier emplacement vide : c'est lui qui garde l'ancien `li-pick`.
  ajouterTestID?: string;
  testID?: string;
}

/**
 * Rangée d'emplacements pour des captures d'écran ORDONNÉES : on lit les captures dans l'ordre où
 * elles sont posées. Une capture posée montre sa vignette, son rang et une croix pour la retirer ;
 * les emplacements libres, en pointillés, ouvrent la galerie. Quand les places sont toutes prises, plus
 * aucun emplacement libre : on ne propose pas d'ajouter ce qui ne tiendrait pas.
 */
export function GrilleCaptures({
  images,
  max,
  onAdd,
  onRemove,
  libelleAjouter,
  libelleRetirer,
  ajouterTestID,
  testID,
}: GrilleCapturesProps) {
  const places = Array.from({ length: max }, (_, i) => i);
  const premierVide = images.length;
  return (
    <View testID={testID} style={styles.grille}>
      {places.map((i) => {
        const image = images[i];
        if (image) {
          return (
            <View key={`${i}-${image.uri}`} testID={testID ? `${testID}-${i}` : undefined} style={styles.case}>
              <Image source={{ uri: image.uri }} style={styles.vignette} />
              <View style={styles.rang} pointerEvents="none">
                <Text style={styles.rangTexte}>{i + 1}</Text>
              </View>
              <Pressable
                testID={testID ? `${testID}-retirer-${i}` : undefined}
                accessibilityRole="button"
                accessibilityLabel={libelleRetirer(i + 1)}
                onPress={() => onRemove(i)}
                hitSlop={12}
                style={styles.retirer}
              >
                <X size={11} color={colors.white} strokeWidth={3} />
              </Pressable>
            </View>
          );
        }
        return (
          <Pressable
            key={`vide-${i}`}
            testID={i === premierVide ? ajouterTestID : testID ? `${testID}-vide-${i}` : undefined}
            accessibilityRole="button"
            accessibilityLabel={libelleAjouter}
            onPress={onAdd}
            style={[styles.case, styles.vide]}
          >
            <Plus size={16} color={FEATURE_ACCENTS.linkedin.color} strokeWidth={2.4} />
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  grille: { flexDirection: 'row', gap: spacing.sm },
  // Carrées : `flex: 1` partage la largeur, `aspectRatio` fixe la hauteur. À 390 pt, ~58 pt de côté.
  case: { flex: 1, minWidth: 0, aspectRatio: 1, borderRadius: formes.rayon.sm },
  vide: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#CDB5F0',
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vignette: { width: '100%', height: '100%', borderRadius: formes.rayon.sm, backgroundColor: colors.grey200 },
  rang: {
    position: 'absolute',
    start: 4,
    bottom: 4,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: radius.pill,
    backgroundColor: colors.night,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rangTexte: { fontSize: 11, fontWeight: '800', color: colors.white },
  retirer: {
    position: 'absolute',
    top: -6,
    end: -6,
    width: 20,
    height: 20,
    borderRadius: radius.pill,
    backgroundColor: colors.grey700,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
